import { CURRENT_YEAR } from '@sly/frontend/common/constants/year';

export default function LegalPageContentPrivacy() {
  return (
    <div
      className='legal-page-content'
      data-tp-id='gen-div-12d4db1e-6cf2-445a-8b78-f8a4e8810674'
    >
      <h1>SENIORLY PRIVACY POLICY &amp; NOTICE AT COLLECTION</h1>
      <h2>Last Reviewed/Updated: March 31, {CURRENT_YEAR}</h2>

      <p>
        This Privacy Policy and Notice at Collection (&quot;
        <b>Privacy Policy</b>
        &quot;) describes the types of Personal Information that Seniorly, Inc.
        (&quot;<b>Seniorly</b>,&quot; &quot;<b>we</b>,&quot; &quot;<b>our</b>
        ,&quot; or &quot;<b>us</b>&quot;) collects from and about you, how we
        may use and disclose such information, and your choices and legal rights
        with respect to such information. We ask that you carefully review this
        Privacy Policy, as it applies to the websites (each a &quot;<b>Site</b>
        &quot;) and mobile applications (each an &quot;<b>App</b>&quot;) as well
        as the services owned, operated, and/or provided by Seniorly that
        display or link to this Privacy Policy, including services provided via
        phone or offline (collectively, &quot;<b>Seniorly Services</b>&quot;).
      </p>

      <p>
        <b>
          By using Seniorly Services, you agree to the collection, use, and
          disclosure of your information as described in this Privacy Policy. If
          you do not agree, please do not use Seniorly Services.
        </b>
      </p>

      <p>
        For purposes of this Privacy Policy, &quot;Personal Information&quot;
        refers to information that identifies, relates to, describes, is
        reasonably capable of being associated with, or could reasonably be
        linked, directly or indirectly, to you or your household, and includes
        &quot;personal data&quot; or similar terms as defined in applicable law.
      </p>

      <p>
        For information on our collection and use of &quot;Consumer Health
        Data&quot; as defined by the Washington My Health My Data Act, please
        visit our{' '}
        <a
          href='https://www.carescout.com/legal/privacy-policy-wa'
          target='_blank'
        >
          Washington Privacy Policy
        </a>
        .
      </p>
      <br />

      <h1 id='notice-at-collection'>
        NOTICE AT COLLECTION OF PERSONAL INFORMATION
      </h1>

      <p>
        We collect the categories of Personal Information identified in the
        following chart. We generally disclose these categories of Personal
        Information for business purposes to our service providers, including
        payment processors and account management providers to facilitate
        Seniorly Services and access to the Website. Some of these categories we
        &quot;sell&quot; or &quot;share&quot; for &quot;targeted
        advertising&quot; (as such terms are defined in U.S. law) with business
        partners and third parties, such as data analytics providers,
        advertising technology vendors, third-party advertising networks, and
        social media platforms.
      </p>

      <p>
        We do not knowingly sell or share Personal Information about persons
        under the age of 18.
      </p>

      <p>
        Please{' '}
        <a
          href='#'
          role='button'
          onClick={(e) => {
            e.preventDefault();
            (window as any)?.ketch?.('showExperience');
          }}
        >
          click here
        </a>{' '}
        to opt out of the sale or sharing of your Personal Information,
        including for targeted advertising. You may also choose to enable
        online, where available, a universal tool that automatically
        communicates your opt-out preferences, such as the Global Privacy
        Control (&quot;<b>GPC</b>&quot;). We will process the GPC signal as a
        request to opt-out.
      </p>
      <br />

      <h2>a. Categories of Personal Information We Collect</h2>

      <table
        className='w-full overflow-hidden rounded-md'
        style={{
          background: '#f3f7fa', // blueish gray
        }}
      >
        <thead>
          <tr>
            <th
              className='bg-[#e6eef3] px-4 py-3 text-left text-base font-bold'
              style={{ color: '#233a52' }}
            >
              Categories of Personal Information Collected
            </th>
          </tr>
        </thead>
        <tbody className='text-[15px]'>
          <tr>
            <td className='border-t border-[#d9e2ea] px-4 py-3'>
              <b>Identifiers/biographical information</b>, including contact
              information (name, email address, postal address, telephone
              number, signature), Internet Protocol (&quot;IP&quot;) address and
              similar online identifiers.
            </td>
          </tr>
          <tr>
            <td className='border-t border-[#d9e2ea] px-4 py-3'>
              <b>
                Characteristics of protected classifications under California or
                Federal law
              </b>
              : Medical condition and medical information.
            </td>
          </tr>
          <tr>
            <td className='border-t border-[#d9e2ea] px-4 py-3'>
              <b>Internet or other electronic network activity information</b>,
              including IP address, imprecise location information, device
              identifiers, advertising identifiers, browsing history, diagnostic
              information, and other information collected from and about your
              interaction with us and the Site.
            </td>
          </tr>
          <tr>
            <td className='border-t border-[#d9e2ea] px-4 py-3'>
              <b>Inferences</b>, meaning inferences drawn from the above-listed
              categories of Personal Information (excluding medical condition or
              medical information) to create a consumer profile reflecting
              consumer preferences or characteristics.
            </td>
          </tr>
          <tr>
            <td className='border-t border-[#d9e2ea] px-4 py-3'>
              <b>Sensitive Personal Information</b>: Personal Information
              collected and analyzed concerning your health. We do not use or
              disclose sensitive Personal Information for the purpose of
              inferring characteristics about you or for any purpose other than
              the limited permissible purposes set forth in applicable law.
            </td>
          </tr>
        </tbody>
      </table>
      <br />

      <h2>
        b. The Business and Commercial Purposes for Which We Collect and Use
        Your Personal Information
      </h2>

      <ul className='ml-8 list-disc'>
        <li>To provide you with Seniorly Services.</li>
        <li>To process payments.</li>
        <li>
          To create, maintain, customize, and secure your account with us.
        </li>
        <li>
          To communicate with you, such as through bots, including to respond to
          your inquiries/requests and request feedback from you, and to send you
          important updates and messages about changes to Seniorly Services,
          this Privacy Policy, and/or other applicable terms and conditions.
        </li>
        <li>
          To review the usage and maintain the operation of Seniorly Services.
        </li>
        <li>
          To conduct analysis and develop and/or improve our products and
          Seniorly Services.
        </li>
        <li>
          To monitor, protect, and maintain the security and integrity of
          Seniorly Services and our business, such as protecting against and
          preventing fraud, unauthorized transactions, claims and other
          liabilities.
        </li>
        <li>
          To comply with applicable laws and regulations and respond to lawful
          requests and communications from law enforcement and other government
          officials.
        </li>
        <li>
          To carry out sales and business transactions in which information held
          by us is among the assets transferred or is otherwise relevant to the
          evaluation, negotiation, or completion of the transaction.
        </li>
        <li>
          To protect our rights, privacy, safety, property and/or those of
          others.
        </li>
        <li>
          To fulfill any other purpose for which you provide your Personal
          Information or as explained to you at the point of information
          collection.
        </li>
        <li>To provide you with customized content or targeted offers.</li>
        <li>To engage in data driven targeted advertising.</li>
        <li>
          To send you information, newsletters, and marketing/promotional
          material from us and, or on behalf of, our marketing partners and
          affiliates.
        </li>
      </ul>
      <br />

      <h2>c. Retention</h2>

      <p>
        We will retain your Personal Information for as long as reasonably
        necessary to provide you with the Seniorly Services that you request,
        for marketing purposes unless you opt out as described in our Privacy
        Policy, or otherwise where permitted or required in accordance with
        applicable law. We will retain and use your Personal Information as
        necessary to comply with our legal obligations, resolve disputes, and
        enforce our agreements. What this means in practice will vary between
        different types of information, and when we consider our approach we
        take into account ongoing business or legal needs for the information,
        for example in relation to tax, health and safety, and potential or
        actual disputes or investigations.
      </p>
      <br />

      <h1>PERSONAL INFORMATION WE COLLECT</h1>

      <p>
        There are several ways we may obtain Personal Information about you,
        including through (A) information you provide to us or others provide on
        your behalf; (B) information we automatically collect; (C) information
        we receive from third parties; and (D) combining information from
        different sources. This includes data that identifies you personally
        whether directly or indirectly.
      </p>
      <br />

      <h2>a. Information You Provide</h2>

      <p>
        We collect Personal Information as listed in the above Notice at
        Collection from you as follows:
      </p>

      <ul className='ml-8 list-disc'>
        <li>
          <b>When You Use Seniorly Services.</b>&nbsp;We collect Personal
          Information from you when you inquire about or create an account to
          use Seniorly Services, or when a third party inquires about or uses
          Seniorly Services on your behalf.
        </li>
        <li>
          <b>When You Make a Purchase.</b>&nbsp;If you purchase services from
          us, we or our third-party payment processors collect a payment card
          number or other payment information.
        </li>
        <li>
          <b>When You Provide Information About a Third Party.</b>&nbsp;If you
          send someone else a communication from Seniorly Services, such as
          sending an article or community profile to a friend, the Personal
          Information you provide (e.g., name, contact information) may be used
          to facilitate communication with such person.
        </li>
      </ul>
      <br />

      <h2>b. Information We Automatically Collect</h2>

      <p>
        We and our third-party partners and service providers use a variety of
        technologies to automatically collect certain information from you when
        engaging with our Site and Seniorly Services. We may collect your
        Personal Information using different data collection technologies, such
        as cookies, pixels, and tags on our Site, and in email messages and
        certain online advertisements. For information on the choices you may
        have with respect to these data collection technologies, see the
        &quot;Your Privacy Choices&quot; section below.
      </p>

      <p>
        The specific types of Personal Information that we, our partners, and
        service providers may automatically collect when you visit our Site and
        interact with Seniorly Services include:
      </p>

      <ul className='ml-8 list-disc'>
        <li>
          <b>Device and Browser Information.</b>&nbsp;This includes your
          device&apos;s IP address and/or other unique identifiers, browser
          type, device type, internet service provider, operating system, and
          when you access Seniorly Services from a mobile device, your
          device&apos;s approximate location (derived from your device&apos;s IP
          address or other signals).
        </li>
        <li>
          <b>Usage Information.</b>&nbsp;When you interact with Seniorly
          Services, certain information may be collected by us or our third
          party vendors, including the date and time of your visit, the pages
          you view immediately before and after you access Seniorly Services,
          the areas or pages of Seniorly Services that you visit, the amount of
          time you spend viewing or using Seniorly Services, items placed or
          left in your shopping cart on the Site or App, keystrokes, mouse
          movements, form field entries, recordings of chat sessions or your use
          of and inputs to other AI-supported tools, and other use and overall
          engagement with Seniorly Services. We may also collect user experience
          recordings that reproduce your interactions with Seniorly Services,
          including mouse movements, page scrolling, and information you type
          (including Personal Information), screen taps, and other actions you
          take while using Seniorly Services. Recordings may include technical
          and usage data, as well as visual representations of actions you take
          while using Seniorly Services. We use these recordings to help us
          understand how users interact with Seniorly Services and to design a
          better user experience for you.
        </li>
        <li>
          <b>Marketing Information.</b>&nbsp;If you receive an email from us,
          information may be collected about your interactions with the message
          (e.g., whether you opened, forwarded, or clicked through to our Site).
        </li>
        <li>
          <b>Third-party analytics technologies.</b>&nbsp;We may use third-party
          analytics tools to better understand who is using Seniorly Services,
          how people are using them, and how to improve their effectiveness as
          well as the effectiveness of any related content.
        </li>
      </ul>

      <p>
        <b>Cookies and Other Similar Technologies:</b>&nbsp;&quot;Cookies&quot;
        are text files that are placed on your browser by the websites that you
        visit. Cookies are used for various purposes, including to distinguish
        you from other users, make your site navigation more efficient, help
        remember your preferences, enhance your browsing experience, and improve
        the use and functionality of our Sites and related content. They can
        also enable the delivery of relevant and personalized advertisements to
        you across the Internet.
      </p>

      <p>
        Please note that cookies and other similar technologies provided by
        third parties may be placed on our Sites. The providers of these
        technologies may create one or more identifiers associated with you or
        your device(s) both on and off our sites and in our apps, and combine
        this information with other sources and use the combined information for
        analytics and/or advertising purposes.
      </p>

      <p>
        <b>Our use of Google Analytics and Advertising technologies:</b>
        &nbsp;Our website uses technology provided by Google to facilitate
        advertising and reporting. We implement Google Analytics cookies to
        collect Personal Information about you when you visit our Site for
        purposes of remarketing, impression reporting, and demographic and
        interest reporting. This information includes browsing and Site usage
        data and can be combined with other data related to you collected
        through Google&apos;s advertising cookie or other third-party
        technology. You can opt out of the use of this technology through the
        Google Analytics{' '}
        <a href='https://tools.google.com/dlpage/gaoptout/'>
          opt-out browser add-on
        </a>
        , Google Ad Settings, and/or Ad Settings for mobile apps.
      </p>
      <br />

      <h2>c. Information We Receive from Third Parties</h2>

      <p>
        We may receive Personal Information from other sources. Our third-party
        sources may include:
      </p>

      <ul className='ml-8 list-disc'>
        <li>
          <b>Our Business Partners.</b>&nbsp;Including companies that co-sponsor
          our promotions.
        </li>
        <li>
          <b>Our Marketing/Advertising and Analytics Partners.</b>
          &nbsp;Including online advertising networks and analytics providers
          that assist us in engaging in targeted advertising.
        </li>
        <li>
          <b>Other Companies.</b>&nbsp;Including data aggregators and other
          similar companies that help supplement the information we maintain
          about you. Certain third parties might link your name or email address
          to other information they have collected, such as your past
          online/offline purchases and your online usage information.
        </li>
      </ul>
      <br />

      <h2>d. Combination of Information</h2>

      <p>
        We may combine Personal Information that we receive from various
        sources. For example, we may combine Personal Information that we
        collect from you offline with Personal Information we collect from you
        through Seniorly Services. Similarly, we may combine Personal
        Information that we receive from third parties with Personal Information
        we already have about you. We use, disclose, and protect combined
        Personal Information as described in this Privacy Policy.
      </p>
      <br />

      <h1>HOW WE DISCLOSE YOUR PERSONAL INFORMATION</h1>

      <p>
        We may disclose each category of your Personal Information that we
        collect in a number of ways. We disclose your Personal Information for
        business purposes to the following categories of recipients:
      </p>

      <ul className='ml-8 list-disc'>
        <li>
          <b>Service Providers.</b>&nbsp;We disclose your Personal Information
          to our service providers that provide business, professional, or
          technical support services to us, help us operate our business and
          Seniorly Services, or administer activities on our behalf. We require
          our service providers to only use your Personal Information in
          connection with providing services to Seniorly.
        </li>
        <li>
          <b>Research Companies.</b>&nbsp;We may share your Personal Information
          with companies that administer surveys or conduct research projects in
          partnership with us or on our behalf.
        </li>
        <li>
          <b>Providers and Communities.</b>&nbsp;We may share your Personal
          Information with home care providers or senior living communities as
          part of providing Seniorly Services.
        </li>
        <li>
          <b>Partners.</b>&nbsp;For the purpose of providing Seniorly Services,
          we may disclose your Personal Information to a long-term care insurer,
          employer, affinity group, or other entity that has contracted with
          Seniorly to provide its policyholders, employees, affinity group
          members, or customers with access to Seniorly Services.
        </li>
        <li>
          <b>Corporate Affiliates.</b>&nbsp;We may disclose your Personal
          Information with our affiliated companies or brands for purposes
          consistent with this Policy and other business and operational
          purposes as permitted by applicable law.
        </li>
      </ul>

      <p>
        We may also disclose your Personal Information to the following
        categories of third parties:
      </p>

      <ul className='ml-8 list-disc'>
        <li>
          <b>Advertising Partners.</b>&nbsp;We may disclose your Personal
          Information to third parties for targeted advertising, including
          social media platforms, third-party advertising networks, and other
          parties that assist us in serving and optimizing our advertisements.
        </li>
        <li>
          <b>Relevant Third Parties as Part of a Corporate Transaction.</b>
          &nbsp;In the event of a reorganization, merger, sale, joint venture,
          assignment, transfer, or other disposition of all or any portion of
          our business, assets, or stock (including in connection with a
          bankruptcy or similar proceeding), we may disclose or transfer your
          Personal Information to certain third parties, such as the acquiring
          entity and its advisers.
        </li>
        <li>
          <b>Competent Governmental, Regulatory, and Public Authorities.</b>
          &nbsp;We may disclose your Personal Information to government and law
          enforcement authorities, in each case to comply with legal or
          regulatory obligations or requests.
        </li>
        <li>
          <b>Other Third Parties.</b>&nbsp;We may disclose your Personal
          Information to other parties at your direction or with your consent,
          where required by law, or as we believe necessary or appropriate
          either to: (i) comply with applicable law; (ii) protect our operations
          and those of any of our affiliates; (iii) investigate and prevent
          against fraud; (iv) protect our rights, privacy, safety, or property
          and/or those of others; or (v) allow us to pursue available remedies
          or limit damages that we may sustain.
        </li>
      </ul>

      <p>
        We may disclose your Personal Information for other reasons that we will
        describe at the time of information collection or prior to disclosing
        your information.
      </p>

      <p>
        Please note that we may de-identify or aggregate Personal Information so
        that it will no longer be considered Personal Information and disclose
        such information to other parties for purposes consistent with those
        described in this Privacy Policy. We will protect de-identified
        information in accordance with applicable law and will never attempt to
        &quot;re-identify&quot; the information.
      </p>
      <br />

      <h1>Consumers Acting in a Professional Capacity</h1>

      <p>
        In addition to the information above, if you are a California resident
        acting in a professional capacity in your relationship with Seniorly,
        you also have rights under the California Consumer Privacy Act.
      </p>
      <br />

      <h2>
        a. Seniorly Advisors, Premier Advisors, and other Independent
        Contractors
      </h2>

      <p>
        The categories of Personal Information we may collect about Advisors,
        Premier Advisors, or other independent contractors include the
        following:
      </p>

      <ol className='ml-8 list-decimal'>
        <li>
          Identifiers such as a real name, alias, postal address, unique
          personal identifier, online identifier, Internet Protocol address,
          email address, account name, social security number, date of birth,
          driver&apos;s license number, passport number, or other similar
          identifiers that are capable of being associated with a particular
          individual.
        </li>
        <li>
          Professional or employment-related information, such as your
          employment history.
        </li>
        <li>
          Education information, such as your school records or degrees earned.
        </li>
        <li>
          In certain circumstances, other personal information you may
          voluntarily provide such as your signature, insurance policy
          number(s), physical characteristics or description, telephone number,
          financial account information and your financial, medical or health
          insurance information.
        </li>
        <li>
          Following contracting, we may collect characteristics of protected
          classifications under California or federal law provided voluntarily
          by employees, such as race or sex.
        </li>
        <li>
          Internet or other electronic network activities, including, but not
          limited to, browsing history, search history, and information
          regarding your interaction with an Internet Web site, application, or
          advertisement.
        </li>
        <li>Audio and/or visual data, such as call or meeting recordings.</li>
      </ol>

      <p>
        The categories of sensitive Personal Information we may collect about
        Advisors, Premier Advisors, or other independent contractors might
        include the following:
      </p>

      <ol className='ml-8 list-decimal'>
        <li>
          Social security, driver&apos;s license, state identification card, or
          passport number.
        </li>
        <li>Citizenship or immigration status.</li>
        <li>
          Bank or financial account and routing numbers provided voluntarily by
          employees.
        </li>
        <li>
          Following contracting, demographic information including racial or
          ethnic information.
        </li>
        <li>
          The contents of your mail, email, and text messages accessed from, in
          connection with, our systems.
        </li>
        <li>Certain voluntarily provided health information.</li>
      </ol>

      <p>
        The purposes for which we collect the above categories of Personal
        Information and sensitive Personal Information include:
      </p>

      <ol className='ml-8 list-decimal'>
        <li>
          Reviewing and assessing your contract including background checks and
          consumer reports containing information on your character, general
          reputation, personal characteristics.
        </li>
        <li>Reviewing and managing your role, conduct and performance.</li>
        <li>Managing and administering your pay.</li>
        <li>
          Supporting Seniorly&apos;s efforts in maintaining a safe and
          supportive work environment.
        </li>
        <li>
          Researching, reviewing, analyzing and/or developing business or
          product plans or operations.
        </li>
        <li>
          Preventing, detecting, and addressing security incidents and illegal
          activity.
        </li>
        <li>
          Meeting any legal, regulatory, or court-ordered requirements
          associated with your contract.
        </li>
      </ol>

      <p>
        We generally retain Personal Information and sensitive Personal
        Information that we collect about Advisors, Premier Advisors, or other
        independent contractors for as long as needed for regulatory or business
        purposes.
      </p>
      <br />

      <h2>b. Other Consumers Acting in a Professional Capacity</h2>

      <p>
        Consumers acting in a professional capacity include, but are not limited
        to, individuals who are employed by our service providers, individuals
        who are employed with companies with whom we are contemplating a
        business relationship or have established a business relationship,
        individuals employed by companies or organizations where Seniorly
        provides services to those companies or organizations, and independent
        contractors. The categories of information we may collect about these
        types of consumers might include the following:
      </p>

      <ol className='ml-8 list-decimal'>
        <li>
          Identifiers such as a real name, alias, postal address, unique
          personal identifier, online identifier, Internet Protocol address,
          email address, account name, professional number, social security
          number, driver&apos;s license number, passport number, or other
          similar identifiers that are capable of being associated with a
          particular individual.
        </li>
        <li>
          Personal Information in addition to the identifiers listed above, such
          as your signature, insurance policy number(s), physical
          characteristics or description, telephone number, and business
          financial account information and your financial, medical or health
          insurance information.
        </li>
        <li>
          Characteristics of protected classifications under California or
          federal law, such as your race or sex.
        </li>
        <li>
          Commercial information, including records of personal property,
          products or services purchased, obtained, or considered, or other
          purchasing or consuming histories or tendencies.
        </li>
        <li>
          Internet or other electronic network activity information, including,
          but not limited to, browsing history, search history, and information
          regarding your interaction with an Internet Web site, application, or
          advertisement.
        </li>
        <li>Geolocation data, such as device location.</li>
        <li>
          Audio, electronic, visual, thermal, olfactory, or similar information,
          such as call or meeting recordings.
        </li>
        <li>
          Professional or employment-related information, such as your
          employment history.
        </li>
        <li>
          Education information, such as your school records or degrees earned.
        </li>
      </ol>

      <p>
        The categories of sensitive Personal Information, a sub-category of
        Personal Information, we may collect about consumers acting in a
        professional capacity may include the following:
      </p>

      <ol className='ml-8 list-decimal'>
        <li>
          A consumer&apos;s social security, driver&apos;s license, state
          identification card, or passport number.
        </li>
        <li>
          A consumer&apos;s ethnic origin for diversity reporting purposes.
        </li>
      </ol>

      <p>
        The purposes we may collect the above categories of Personal Information
        and sensitive Personal Information may include one or more of the
        following:
      </p>

      <ol className='ml-8 list-decimal'>
        <li>
          Marketing our products and services to you, including responding to
          your requests for product or service information.
        </li>
        <li>
          Understanding what products or services you or your company can offer
          to us.
        </li>
        <li>Researching and analyzing our marketing efforts.</li>
        <li>
          Responding to legal requests and complying with regulatory
          requirements.
        </li>
        <li>Auditing our interactions and concurrent transactions with you.</li>
        <li>
          Detecting security incidents, protecting against malicious, deceptive,
          fraudulent, or illegal activity.
        </li>
        <li>
          Debugging to identify and repair errors that impair existing intended
          functionality of our websites and other interactions with you.
        </li>
        <li>
          Short-term, transient use, when your personal information is not
          disclosed to another third party and is not used to build a profile
          about you or otherwise alter your experience outside the current
          interaction.
        </li>
        <li>
          Maintaining or servicing accounts, contracts, or other relationships
          with you or your company, so that either party may provide: customer
          service, processing, or fulfilling orders and transactions, verifying
          customer information, processing payments, providing analytic
          services, or providing similar services.
        </li>
        <li>
          Undertaking internal research for technological development and
          demonstration.
        </li>
        <li>
          Undertaking activities to verify or maintain the quality or safety of
          a service or device that is owned, manufactured, manufactured for, or
          controlled by us, and to improve, upgrade, or enhance the service or
          device that is owned, manufactured, manufactured for, or controlled by
          us.
        </li>
      </ol>

      <p>
        We may retain Personal Information and sensitive Personal Information
        that we collect about consumers acting in a professional capacity for as
        long as needed for business or regulatory purposes.
      </p>
      <br />

      <h1>YOUR PRIVACY CHOICES AND RIGHTS</h1>

      <p>
        Seniorly provides you a number of choices with respect to how we collect
        and process your Personal Information.
      </p>
      <br />

      <h2>a. State Privacy Rights</h2>

      <p>
        Residents of California, Connecticut, Maryland, New Hampshire, New
        Jersey, Texas, Virginia, or any other state with an applicable privacy
        law have certain rights relating to the collection, use, disclosure, and
        other processing of their Personal Information. The exact scope of these
        rights may vary by state and may vary depending on certain exceptions:
      </p>

      <ul className='ml-8 list-disc'>
        <li>
          <b>Right to Know.</b>&nbsp;You may have the right to know what
          Personal Information we have collected about you, including the
          categories of sources from which the Personal Information is
          collected, the business or commercial purpose for collecting, selling,
          or sharing Personal Information, the categories of third parties to
          whom we disclose Personal Information, the categories of Personal
          Information disclosed to third parties, and the specific pieces of
          Personal Information we have collected about you. You may also have
          the right to access this Personal Information in a portable format.
        </li>
        <li>
          <b>Right to Request Categories of Third Parties.</b>&nbsp;You may have
          the right to request a list of the categories of third parties with
          which we disclose your Personal Information.
        </li>
        <li>
          <b>Right to Request Specific List of Third Parties.</b>&nbsp;You may
          have the right to request a list of the specific third parties to
          which we disclose your Personal Information.
        </li>
        <li>
          <b>Right to Delete.</b>&nbsp;You may have the right to request that we
          delete Personal Information that we have collected from or about you,
          subject to certain exceptions.
        </li>
        <li>
          <b>Right to Correct.</b>&nbsp;You may have the right to correct
          inaccurate Personal Information that we may maintain about you,
          subject to appropriate verification. Where appropriate, we may comply
          with a right to correct by deleting your Personal Information.
        </li>
        <li>
          <b>Right to Opt Out of Sale and Targeted Advertising.</b>&nbsp;We use
          and disclose to third parties Personal Information for analytics and
          advertising purposes which may be considered a &quot;sale&quot; under
          applicable state laws. Accordingly, you may have the right to opt out
          of the sale or &quot;sharing&quot; of your Personal Information, or
          the use and disclosure of your Personal Information for &quot;targeted
          advertising&quot; (as these terms are defined in applicable law).
        </li>
      </ul>

      <p>
        To exercise your right to opt out of sale and targeted advertising,
        please click{' '}
        <a
          href='#'
          role='button'
          onClick={(e) => {
            e.preventDefault();
            (window as any)?.ketch?.('showExperience');
          }}
        >
          here
        </a>{' '}
        or click the <b>“Do Not Sell or Share My Personal Information”</b> link
        in the footer of the Site. Not Sell or Share My Personal Information”
        link in the footer of the Site.
      </p>

      <p>
        In some jurisdictions, you may choose to enable a tool that
        automatically communicates your opt-out preferences to all businesses
        that you interact with online. If you enable a browser-based opt-out
        preference signal, such as the GPC, upon receipt or detection, we will
        treat the signal as a valid request to opt out of sale, sharing, or
        targeted advertising linked to that browser and, where we can do so,
        consumer profiles that we have associated with that browser. Please note
        that if you use different browsers or browser profiles, you will have to
        enable the signal on each one that you use. We will never ask you to
        verify your identity to exercise your right to opt out of the sale of
        your Personal Information.
      </p>

      <p>
        To exercise all other privacy rights, please contact us by phone at{' '}
        <a href='tel:8005711918'>800.571.1918</a> or email us at{' '}
        <a href='mailto:privacy@carescout.com'>privacy@carescout.com</a>. If you
        make a request through email, please include the following information
        and the statement &quot;Your Privacy Rights&quot; in the subject field
        of your email:
      </p>

      <ol className='ml-8 list-decimal'>
        <li>Your first and last name;</li>
        <li>
          Whether you are a current or former customer, employee, Advisor,
          Premier Advisor, or business contact;
        </li>
        <li>Your request type;</li>
        <li>Your telephone number;</li>
        <li>Your email address; and</li>
        <li>Any other information you wish to provide.</li>
      </ol>

      <p>
        If you make a request by phone, please provide your name, phone number,
        email address, whether you are a current or former customer, employee,
        Advisor, Premier Advisor, or business contact, your request type, and
        any other information you wish to provide. We will use the information
        provided to us to verify your identity.
      </p>

      <p>
        To protect the confidentiality of yours and others&apos; Personal
        Information, we will only complete your request when your identity has
        been verified (other than for requests to opt out). We will seek to
        match the information in your request to the Personal Information we
        maintain about you. As part of our verification process, we may ask you
        to: submit additional information, use identity verification services to
        assist, or, if you have set up an account on our website, to sign into
        your account as part of our identity verification process. Where
        permitted under applicable law, we may decline a request if we are
        unable to verify your identity (or an agent&apos;s authority to make the
        request) and confirm the Personal Information we maintain relates to
        you.
      </p>

      <p>
        <b>Authorized agents.</b>&nbsp;In certain states, consumers may
        designate an authorized agent to exercise their privacy rights. You may
        designate an authorized agent to submit requests on your behalf using
        the methods described in this section. However, we may require written
        proof of the agent&apos;s permission to do so and verify your identity
        directly with you.
      </p>

      <p>
        <b>Right to appeal.</b>&nbsp;Depending on your U.S. state residency, you
        may have the right to appeal a decision we have made in connection with
        your privacy rights request. To appeal a refusal to take action on your
        request, please contact{' '}
        <a href='mailto:privacy@carescout.com'>privacy@carescout.com</a> or{' '}
        <a href='tel:8005711918'>800.571.1918</a>. If you are unsatisfied with
        the way that we have handled your appeal, you may have the right to
        complain to your state&apos;s attorney general.
      </p>

      <p>
        <b>Right to non-discrimination.</b>&nbsp;You will not receive
        retaliatory or discriminatory treatment in connection with a request to
        exercise your privacy rights described in this section. However, the
        exercising of the rights described above may result in a different
        price, rate, or quality/level of product or service where that
        difference is reasonably related to the impact the right has on our
        relationship with you or is otherwise permitted by law.
      </p>

      <p>
        You are not required to create an account to exercise your state privacy
        rights.
      </p>
      <br />

      <h2>b. Interest-Based Advertising</h2>

      <p>
        Self-regulatory industry organizations such as the Digital Advertising
        Alliance (DAA) offer browser-based opt-out tools for the companies that
        participate in them. Please visit the DAA&apos;s AdChoices site for more
        information:{' '}
        <a href='https://youradchoices.com/control'>
          https://youradchoices.com/control
        </a>
        .
      </p>
      <br />

      <h2>c. Marketing and Promotional Communications</h2>

      <p>
        From time to time, we may send you marketing and promotional
        communications, including special offers from us or our partners. If you
        no longer wish to receive promotional and marketing emails from us, you
        may opt out of such communications at any time by following the opt-out
        instructions included in any promotional or marketing email you receive
        from us.
      </p>
      <br />

      <h1>TEXT MESSAGES</h1>

      <p>
        Messages may be sent by automatic telephone dialing system. You
        acknowledge that these messages are not otherwise secured when they are
        sent, and that there is a risk the message could be intercepted in its
        transmission to you. You acknowledge that these messages may be subject
        to fees from your cellular provider, message and data rates apply, and
        that we and your wireless carrier will not be held liable for any
        delayed or undelivered messages. To disable or update alert(s) or to
        update your mobile phone number, you can update your Notification
        Preferences in your account if applicable, or text HELP for assistance
        or STOP to cancel.
      </p>

      <p>
        You are not required to opt in to receive marketing text message alerts
        (directly or indirectly) as a condition of transacting with us.
        Frequency of messages will vary.
      </p>
      <br />

      <h1>EXTERNAL LINKS</h1>

      <p>
        Our Sites may have links to third-party services or websites, which may
        have privacy policies that differ from our own. We are not responsible
        for the information collection practices of such other services or
        websites, nor does the inclusion of a link on the Sites imply
        endorsement of the linked site or service by us or by our affiliates. We
        cannot guarantee how those services or websites use cookies, pixels or
        other data aggregating or analytics tools, or whether they use cookies
        or other tools that may be able to identify you personally. You should
        carefully review the privacy policies of each website you visit to
        understand how they collect, share, sell, use, and disclose Personal
        Information.
      </p>
      <br />

      <h1>CHILDREN&apos;S PRIVACY</h1>

      <p>
        Seniorly Services are intended for a general audience. We do not direct
        Seniorly Services to individuals under eighteen (18), nor do we
        knowingly solicit or collect any Personal Information from minors.
      </p>
      <br />

      <h1>DATA SECURITY</h1>

      <p>
        We have implemented physical, administrative, and technical safeguards
        to maintain the security, confidentiality, and integrity of your
        information. However, as no transmission of information over the
        internet is absolutely secure, we cannot guarantee the safety of your
        information.
      </p>
      <br />

      <h1>REVISIONS TO THIS PRIVACY POLICY</h1>

      <p>
        We reserve the right to update or modify this Policy at any time without
        prior notice by posting a revised version of the policy on the Site.
        Your use of the Site or any Seniorly Services following revision to this
        Policy constitutes your agreement that all information collected from or
        about you after the revised policy is posted will be subject to the
        terms and conditions of the revised policy. The date listed above
        indicates the most recent change or update to this Policy.
      </p>
      <br />

      <h1>HOW TO CONTACT US</h1>

      <p>
        If you have any questions or concerns about this Privacy Policy or the
        practices described herein, you may contact us at{' '}
        <a href='mailto:privacy@carescout.com'>privacy@carescout.com</a>, by
        mail to 11011 West Broad Street, Glen Allen, VA 23060, or by phone
        at&nbsp;
        <a href='tel:8005711918'>800.571.1918</a>.
      </p>
    </div>
  );
}
